<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { isEmpty } from 'lodash';
import CoverView from '@/views/shared/CoverView.vue';
import MediaPreview from '@/overlays/MediaPreviewOverlay.vue';
import MediaList from '@/views/shared/MediaList.vue';
import { useGalleriesStore } from '@/stores/galleries';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import TabSwitch, { ITab } from '@/components/shared/TabSwitch.vue';
import i18n from '@/utils/i18n';

const galleriesStore = useGalleriesStore();
const route = useRoute();

const MEDIA_TYPES: ITab[] = [
  {
    title: i18n.global.t('global.images'),
    value: 'pictures',
    icon: ['fas', 'fa-image'],
  },
  {
    title: i18n.global.t('ourHome.videos'),
    value: 'videos',
    icon: ['fas', 'fa-film'],
  },
];

const mediaType = ref<ITab>(MEDIA_TYPES[0]);
const selectedMediaIndex = ref(-1);

const galleryEntry = computed((): any => {
  return galleriesStore.galleryEntry;
});

const mediaItems = computed(() => {
  switch (mediaType.value?.value) {
    case 'videos':
      return galleryEntry.value.videos;
    case 'pictures':
    default: // Default to pictures if nothing is specified.
      return galleryEntry.value.pictures;
  }
});

const selectedMediaItem = computed(() => {
  if (isEmpty(galleryEntry.value)) {
    return;
  }

  return mediaItems.value?.[selectedMediaIndex.value] || [];
});

const hasSelectedMediaItem = computed(() => {
  return !isEmpty(selectedMediaItem.value);
});

onMounted(() => {
  const id = route?.params?.id || null;
  if (!id) return;

  galleriesStore.getGalleryEntry(id as string);
  mediaType.value = MEDIA_TYPES[0];
});

onUnmounted(() => {
  galleriesStore.resetGalleryEntry();
});

const onTabSelected = (tab: ITab) => {
  mediaType.value = tab;
};
</script>

<template>
  <div class="galleryEntry">
    <MediaPreview
      v-if="hasSelectedMediaItem"
      :selected-item-index="selectedMediaIndex"
      :media-type="mediaType.value"
      :items="mediaItems"
      :allow-archive-remove="false"
      @close-preview="selectedMediaIndex = -1"
    />

    <CoverView
      v-if="galleryEntry.id"
      :image-url="galleryEntry.coverImage.source"
    >
      <div class="galleryContent">
        <TitleBlock :text="galleryEntry.title" />

        <TabSwitch
          :items="MEDIA_TYPES"
          :selectedItem="mediaType"
          @tab-selected="onTabSelected"
          :color="route?.meta?.color as string"
        />

        <MediaList
          class="pt-10"
          :items="mediaItems"
          :media-type="mediaType.value"
          @item-selected="selectedMediaIndex = $event"
        />
      </div>
    </CoverView>
  </div>
</template>

<style lang="scss">
.galleryContent {
  padding: 80px 100px 0px;
}
.video {
  height: 495px;
}
</style>
